import type { JSX, ReactNode } from 'react'

/**
 * ══ KHUNG TRANG — `Card` · `PageHeader` · `Stat` ════════════════════════════════════════════════
 *
 * Chép `proship-trans/src/components/ui.tsx` (G2, kế hoạch `docs/KE-HOACH-DONG-BO-GIAO-DIEN.md` §5).
 * `className` chép NGUYÊN VĂN (C12). Chú thích gốc của bản cũ giữ nguyên, không diễn giải lại.
 *
 * ⭐ VÌ SAO `Card` LÀ THỨ ĐÁNG PORT TRƯỚC NHẤT: bản cũ dùng nó **264 lần** trong phạm vi RT, và số
 *   đo G0 cho thấy **19/21 màn rs-erp có 0 thẻ khung** (`specs/screenshots/doi-chieu/xep-hang.json`).
 *   Không có nó thì mọi màn là chữ trôi trên nền xám — đúng thứ anh nhìn thấy đầu tiên.
 */

/** `ui.tsx:16`. */
export const Card = (
  { children, className = '', onClick }: { children: ReactNode; className?: string; onClick?: () => void },
): JSX.Element => (
  <div onClick={onClick} className={`bg-white rounded-xl border border-slate-100 shadow-sm ${className}`}>
    {children}
  </div>
)

/**
 * `ui.tsx:1364`. Tiêu đề trang có VẠCH MÀU brand bên trái — phong cách ContainerOps (user chốt 08/07).
 * TIÊU ĐỀ KHÔNG XUỐNG DÒNG (user 10/08): thanh công cụ dài trước đây ép tiêu đề vỡ 2-3 dòng
 * (rõ nhất ở AP "Phải trả nhà cung cấp (AP)"). Nay tiêu đề giữ 1 dòng, cụm nút tự xuống hàng.
 */
export const PageHeader = (
  { title, action }: { title: string; action?: ReactNode },
): JSX.Element => (
  <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
    <h1 className="flex items-center gap-2.5 text-xl font-bold text-slate-800 whitespace-nowrap">
      <span className="w-1.5 h-6 rounded-full bg-brand-500 inline-block shrink-0" aria-hidden />
      {title}
    </h1>
    {action}
  </div>
)

// Thẻ KPI phong cách ContainerOps: viền màu nhạt + nền tint theo màu chữ, nội dung CĂN GIỮA.
// Suy màu viền/nền từ prop color (text-amber-600 → viền amber...) nên các trang cũ không phải sửa gì.
const STAT_TONE: [RegExp, string][] = [
  [/amber|yellow/, 'border-amber-200 bg-amber-50/60'],
  [/emerald|green/, 'border-emerald-200 bg-emerald-50/60'],
  [/red|rose/, 'border-red-200 bg-red-50/60'],
  [/sky|blue|indigo/, 'border-sky-200 bg-sky-50/60'],
  [/violet|purple/, 'border-violet-200 bg-violet-50/60'],
  [/brand|orange/, 'border-brand-200 bg-brand-50/60'],
]

/** `ui.tsx:1399`. */
export const Stat = ({
  label, value, sub, color = 'text-slate-700', compact = false, onClick, active = false,
}: {
  label: string
  value: string
  sub?: string
  color?: string
  compact?: boolean
  /** bấm để lọc/nhảy — thẻ thành clickable (con trỏ + ring khi active) */
  onClick?: () => void
  active?: boolean
}): JSX.Element => {
  const tone = STAT_TONE.find(([re]) => re.test(color))?.[1] ?? 'border-slate-200 bg-white'
  /* GỌN LẠI bản compact (user 25/08, đã nhắc nhiều lần "chưa gọn"): giảm thêm 1 nấc padding + cỡ
     chữ nhãn/số/sub so với trước — thẻ compact giờ THẤP HẲN so với bản thường, đúng tinh thần
     "gọn" chứ không chỉ nhỏ hơn 1 chút. Sửa Ở ĐÂY ăn cả 110 chỗ đang dùng compact trong toàn hệ
     thống, không cần sửa từng trang. */
  const inner = (
    <>
      <div className={`${compact ? 'text-[11px]' : 'text-xs'} font-semibold ${color.replace(/-(600|700)/, '-700')} opacity-80`}>{label}</div>
      {/* nowrap: tránh chữ "đ" bị rớt xuống dòng riêng làm thẻ cao gấp đôi (user 19/07) */}
      <div className={`${compact ? 'text-sm' : 'text-2xl'} font-extrabold ${compact ? 'mt-px' : 'mt-0.5'} whitespace-nowrap ${color}`}>{value}</div>
      {sub !== undefined && sub !== '' && <div className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-slate-400 ${compact ? 'mt-px' : 'mt-0.5'}`}>{sub}</div>}
    </>
  )
  const cls = `rounded-xl border text-center ${tone} ${compact ? 'px-2.5 py-1.5' : 'p-4'}`
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${cls} w-full transition hover:shadow-sm cursor-pointer ${active ? 'ring-2 ring-brand-400' : ''}`}>{inner}</button>
    )
  }
  return <div className={cls}>{inner}</div>
}
